// ENG-3355 — one parsing rule, and the date/count display helpers built on it.
//
// Before this, about nine call sites each rolled their own `new Date(...)` — AccountDeletedPage
// (since removed, ENG-3341), the Risk Report, extensionRequests.ts, ManagedAccessPage, OverviewPage,
// extensionAnalysis.ts, riskReportMode.ts, EffectiveProtectionPage and UserDetailView/
// TelemetryEventDrilldown — and disagreed in two different ways that both trace back to the SAME
// one-liner, `new Date(iso)`:
//
//  - A bare calendar date ("2026-08-28", what a Kotlin `LocalDate.toString()` sends) has no time and
//    no zone. ECMA-262 nonetheless parses it as UTC MIDNIGHT, so a negative-offset viewer — anyone in
//    the Americas — reads it back a day early once it's redrawn in their own zone:
//    `new Date("2026-08-28")` shows "8/27/2026" in America/Los_Angeles. That is the actual mechanism
//    behind "the same date differs by a day between pages", not a rounding quirk.
//  - A zone-less date-TIME ("2026-09-03T14:32:00", what a Kotlin `LocalDateTime`/`Instant.toString()`
//    without a zone sends) has no such trap — the same spec reads it as the viewer's OWN local clock,
//    verbatim, in every zone. `extensionRequests.ts` treated this as equally dangerous and hand-built
//    a UTC-anchored, UTC-displayed workaround to dodge a shift that (verified against Node, both
//    hemispheres) never actually happens for this shape. It happened to render the right day, the
//    long way round; nothing here needed to survive that particular defense once the real trap above
//    is closed.
//
// The Risk Report is where both showed up on one page at once: `windowStart`/`windowEnd` (a
// `LocalDate`) were interpolated RAW, unparsed, right next to `generatedAt` (an `Instant`) run
// through `.toLocaleString()` — a machine-format date sitting next to a human one, from the same
// report, with nothing saying whether they even agreed.
//
// The rule below is the one line this module exists to hold everyone to: a bare `YYYY-MM-DD` is read
// as its own Y/M/D, into a local calendar date — never handed whole to `new Date(...)`. Anything else
// (a zone-less local date-time, or a real zoned instant) is a moment, and native `Date` parsing
// already gets it right, so it is passed straight through.

/** A bare `YYYY-MM-DD`, nothing else — the shape a Kotlin `LocalDate.toString()` sends. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The one parsing rule. A date-only string becomes a LOCAL calendar date at midnight, built from its
 * own Y/M/D — never `new Date("2026-08-28")`, which the spec reads as UTC midnight and a
 * negative-offset viewer reads back a day early. Anything else (epoch millis, a zone-less local
 * date-time, or a real zoned instant/ISO string) is a moment and is handed to `new Date(...)`
 * unmodified, which already parses each of those correctly.
 *
 * Building the calendar date by hand means the `Date` constructor's own leniency has to be undone by
 * hand too: `new Date(2026, 12, 45)` does not throw or clamp, it rolls forward to a real date (Feb 14,
 * 2027) - so `"2026-13-45"` would otherwise silently become a real, wrong day instead of the "can't
 * parse this" every other bad string gets. The round-trip check below refuses exactly that: if asking
 * the built Date for its year/month/day back doesn't reproduce what was typed in, the input didn't
 * name a real date, and this returns the same Invalid Date `new Date(input)` would for any other
 * unparseable string - `formatDate`/`formatDateTime` already fall back correctly from that.
 */
export function parseDate(input: string | number): Date {
  if (typeof input === "number") return new Date(input);
  const m = DATE_ONLY.exec(input.trim());
  if (!m) return new Date(input);
  const [, y, mo, d] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const date = new Date(year, month - 1, day);
  const isRealDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealDate ? date : new Date(NaN);
}

/** Locale options shared by both display helpers, so a date reads the same length everywhere. */
const DATE_OPTS: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };

/**
 * Empty input reads as "—" (ENG-3355 dash sweep — never a bare "-" placeholder); a string that fails
 * to parse (including a date-only string whose Y/M/D isn't a real date - see `parseDate`) is returned
 * as given, so a caller never sees "Invalid Date". Shared by every display helper below so the three
 * only differ in how they render an already-valid `Date`.
 */
function displayDate(input: string | number | null | undefined, render: (d: Date) => string): string {
  if (input == null || input === "") return "—";
  const d = parseDate(input);
  if (Number.isNaN(d.getTime())) return typeof input === "string" ? input : "—";
  return render(d);
}

/**
 * A date only, no time — "Sep 20, 2026". For an invite sent, a request filed, a lock's end date: the
 * console has never had a reader who needed the hour, only the day (OverviewPage's own note on invite
 * timestamps).
 */
export function formatDate(input: string | number | null | undefined): string {
  return displayDate(input, (d) => d.toLocaleDateString(undefined, DATE_OPTS));
}

/**
 * A date AND time — "Sep 20, 2026, 3:41 PM". For an actual moment: when a report was generated, when
 * an event occurred, when someone was last seen.
 */
export function formatDateTime(input: string | number | null | undefined): string {
  return displayDate(input, (d) => d.toLocaleString(undefined, { ...DATE_OPTS, hour: "numeric", minute: "2-digit" }));
}

/**
 * `formatDateTime`, plus the viewer's own zone abbreviation — "Sep 20, 2026, 3:41 PM PDT". Only for
 * where the date is read somewhere else than where it was made and that gap matters: the Risk Report
 * is printed to a PDF and handed to a customer, so "Generated by Haven on …" needs to say whose clock
 * it's in. Everywhere else on the console the reader IS the viewer, so `formatDateTime` stays plain -
 * this is deliberately its own helper rather than a flag on it, so the other ~10 call sites cannot
 * pick it up by accident.
 */
export function formatDateTimeWithZone(input: string | number | null | undefined): string {
  return displayDate(input, (d) => d.toLocaleString(undefined, { ...DATE_OPTS, hour: "numeric", minute: "2-digit", timeZoneName: "short" }));
}

/**
 * "1 day" / "3 days" — the one pluralisation rule, so a count is never rendered against a noun that
 * assumed it would be more than one ("1 days observed", OverviewPage). `many` defaults to `one + "s"`,
 * which covers most of this app's nouns; pass it explicitly for the ones that don't ("member" is
 * still "members", but nothing here forces an irregular plural to be wrong).
 */
export function plural(n: number, one: string, many: string = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
